import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  XCircle, 
  ArrowRight, 
  Home, 
  RefreshCw,
  CreditCard
} from 'lucide-react';
import { paymentAPI } from '../../services/api';
import { formatCurrency, formatDateTime } from '../../utils/formatters';
import Loader from '../common/Loader';

const Cancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const sessionId = searchParams.get('session_id');

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await paymentAPI.getPaymentRequestBySession(sessionId);
      if (response.success) {
        setPayment(response.data);
      } else {
        toast.error('Payment details not found');
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchPaymentDetails();
    } else {
      setIsLoading(false);
    }
  }, [sessionId, fetchPaymentDetails]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size="lg" text="Loading payment details..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {/* Cancel Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-6">
            <XCircle className="h-10 w-10 text-red-600" />
          </div>

          {/* Cancel Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Cancelled
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Your payment was cancelled. No charges have been made to your account.
          </p>

          {/* Payment Details */}
          {payment && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Request Details</h2>
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(payment.amount, payment.currency)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Description:</span>
                  <span className="font-medium text-gray-900">{payment.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Requested:</span>
                  <span className="font-medium text-gray-900">
                    {formatDateTime(payment.createdAt)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Expires:</span>
                  <span className="font-medium text-gray-900">
                    {formatDateTime(payment.expiresAt)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Why was it cancelled? */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-900 mb-3">Why was the payment cancelled?</h3>
            <ul className="text-left text-yellow-800 space-y-2">
              <li className="flex items-start">
                <XCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>You chose to cancel the payment during checkout</span>
              </li>
              <li className="flex items-start">
                <RefreshCw className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>You can try again anytime before the payment expires</span>
              </li>
              <li className="flex items-start">
                <CreditCard className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
                <span>No charges were made to your payment method</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {payment?.paymentLink && (
                <a
                  href={payment.paymentLink}
                  className="btn btn-primary flex items-center justify-center"
                >
                  <RefreshCw className="w-5 h-5 mr-2" />
                  Try Again
                </a>
              )}
              
              <Link
                to="/admin/dashboard"
                className="btn btn-secondary flex items-center justify-center"
              >
                <Home className="w-5 h-5 mr-2" />
                Go to Dashboard
              </Link>
            </div>

            <div className="text-center">
              <button
                onClick={() => navigate(-1)}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center justify-center mx-auto"
              >
                <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
                Go Back
              </button>
            </div>
          </div>

          {/* Additional Information */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              If you're having trouble with the payment process, please contact us for assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cancel;
